import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import {
  createTemplateRequest,
  fetchTemplatesRequest,
  resetCreateState,
  updateTemplateStatusRequest,
} from "../../../redux/CustomerCoupon/customerCouponSlice";
import { fetchJewellersSidebarRequest } from "../../../redux/JewellerList/JewellerSlice";
import TableCompo from "../Table/TableCompo";
import { canShowPermission } from "../Permissions/permissionObject";

const emptyForm = {
  jewellerScope: "all",
  jewellers: [],
  title: "",
  subtitle: "",
  description: "",
  customMessage: "",
  type: "lucky_draw",
  status: "active",
};

const jewellerMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 280,
    },
  },
};

const MESSAGE_VARIABLES = [
  { key: "customerName", label: "Customer name" },
  { key: "firstName", label: "First name" },
  { key: "lastName", label: "Last name" },
  { key: "phone", label: "Phone" },
  { key: "couponCode", label: "Coupon code" },
  { key: "couponPoints", label: "Total points" },
  { key: "pointsEarned", label: "Points earned (this txn)" },
  { key: "rewardTitle", label: "Reward title" },
  { key: "rewardSubtitle", label: "Reward subtitle" },
  { key: "orderType", label: "Order type" },
  { key: "amount", label: "Amount" },
];

const previewResolvedMessage = (message) => {
  const sample = {
    customerName: "Rahul Sharma",
    firstName: "Rahul",
    lastName: "Sharma",
    phone: "9876543210",
    couponCode: "ABCD1234",
    couponPoints: "12",
    pointsEarned: "5",
    rewardTitle: "Free silver coin",
    rewardSubtitle: "Lucky draw win",
    orderType: "Buy",
    amount: "5000",
  };
  return String(message || "").replace(
    /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
    (_match, key) => sample[key] ?? "",
  );
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
  },
};

export default function RewardTemplateTab({ permissions }) {
  const dispatch = useDispatch();
  const { templates, templateTotal, templatesLoading, creating, createSuccess } =
    useSelector((state) => state.customerCoupon);
  const { jewellersSidebar = [] } = useSelector((state) => state.jeweller);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [search, setSearch] = useState("");
  const [imageBroken, setImageBroken] = useState(false);
  const [viewImage, setViewImage] = useState({ open: false, url: "", title: "" });

  const canCreate = canShowPermission(permissions, "rewardTemplate", "create");
  const canEdit = canShowPermission(permissions, "rewardTemplate", "edit");

  const listQuery = {
    start: pagination.page * pagination.limit,
    limit: pagination.limit,
    search,
  };

  const jewellerOptions = [...jewellersSidebar].sort((a, b) =>
    (a.jewellerName || "").localeCompare(b.jewellerName || ""),
  );

  useEffect(() => {
    dispatch(fetchJewellersSidebarRequest({ allActive: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchTemplatesRequest({
        start: pagination.page * pagination.limit,
        limit: pagination.limit,
        search,
      }),
    );
  }, [dispatch, pagination, search]);

  useEffect(() => {
    if (!createSuccess) return;
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setForm(emptyForm);
    setImageFile(null);
    setImageBroken(false);
    dispatch(resetCreateState());
  }, [createSuccess, dispatch]);

  useEffect(() => {
    setImageBroken(false);
  }, [previewUrl]);

  const onChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onPickImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setImageBroken(false);
  };

  const onRemoveImage = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setImageFile(null);
    setPreviewUrl("");
    setImageBroken(false);
  };

  const onSubmit = () => {
    if (form.jewellerScope === "custom" && !(form.jewellers || []).length) {
      toast.error("Please select at least one jeweller");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    dispatch(
      createTemplateRequest({
        ...form,
        jewellers: form.jewellerScope === "all" ? [] : form.jewellers,
        imageFile,
      }),
    );
  };

  const onReset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setForm(emptyForm);
    setImageFile(null);
    setPreviewUrl("");
    setImageBroken(false);
  };

  const insertMessageVariable = (variableKey) => {
    const token = `{{${variableKey}}}`;
    setForm((prev) => ({
      ...prev,
      customMessage: prev.customMessage
        ? `${prev.customMessage}${prev.customMessage.endsWith(" ") ? "" : " "}${token}`
        : token,
    }));
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const rows = (templates || []).map((item) => ({
    ...item,
    id: item._id,
    createdAtText: formatDateTime(item.createdAt),
    updatedAtText: formatDateTime(item.updatedAt),
  }));

  const columns = [
    {
      field: "image",
      headerName: "Image",
      render: (value, row) => {
        if (!row.image) {
          return (
            <Typography sx={{ fontSize: 13, color: "#999" }}>No image</Typography>
          );
        }
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              component="img"
              src={row.image}
              alt={row.title || "Reward"}
              onClick={() =>
                setViewImage({
                  open: true,
                  url: row.image,
                  title: row.title || "Reward image",
                })
              }
              sx={{
                width: 48,
                height: 48,
                objectFit: "cover",
                borderRadius: "10px",
                border: "1px solid #eee5ff",
                cursor: "pointer",
                backgroundColor: "#f7f1ff",
              }}
            />
            <Button
              size="small"
              startIcon={<ImageOutlinedIcon />}
              onClick={() =>
                setViewImage({
                  open: true,
                  url: row.image,
                  title: row.title || "Reward image",
                })
              }
              sx={{ textTransform: "none", color: "#7028DA", fontWeight: 600 }}
            >
              View
            </Button>
          </Stack>
        );
      },
    },
    { field: "title", headerName: "Title" },
    {
      field: "jewellerLabel",
      headerName: "Jewellers",
      minWidth: 180,
      render: (value, row) => (
        <Chip
          size="small"
          label={
            row.jewellerScope === "all"
              ? "All"
              : value || (row.jewellerNames || []).join(", ") || "Custom"
          }
          sx={{
            fontWeight: 600,
            backgroundColor:
              row.jewellerScope === "all" ? "#e8f8ef" : "#f3eaff",
            color: row.jewellerScope === "all" ? "#1b7a45" : "#7028DA",
            maxWidth: 220,
          }}
        />
      ),
    },
    { field: "subtitle", headerName: "Subtitle" },
    {
      field: "customMessage",
      headerName: "Custom message",
      minWidth: 280,
      flex: 1.4,
      alignTop: true,
      sortable: false,
      render: (value) => (
        <Typography
          sx={{
            fontSize: 13,
            color: value ? "#444" : "#999",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.45,
            py: 0.5,
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      render: (value) => (
        <Chip
          size="small"
          label={value || "-"}
          sx={{ textTransform: "capitalize", fontWeight: 600 }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      render: (value) => (
        <Chip
          size="small"
          label={value || "-"}
          color={value === "active" ? "success" : "default"}
          sx={{ textTransform: "capitalize", fontWeight: 600 }}
        />
      ),
    },
    {
      field: "createdByName",
      headerName: "Created by",
      minWidth: 150,
      render: (value) => value || "-",
    },
    {
      field: "createdAtText",
      headerName: "Created at",
      minWidth: 170,
    },
    {
      field: "updatedByName",
      headerName: "Updated by",
      minWidth: 150,
      render: (value) => value || "-",
    },
    {
      field: "updatedAtText",
      headerName: "Updated at",
      minWidth: 170,
    },
    {
      field: "action",
      headerName: "Action",
      render: (value, row) => {
        if (!canEdit) return "-";
        const nextStatus = row.status === "active" ? "inactive" : "active";
        return (
          <Button
            size="small"
            variant="outlined"
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              borderColor: nextStatus === "active" ? "#2e7d32" : "#9e9e9e",
              color: nextStatus === "active" ? "#2e7d32" : "#616161",
            }}
            onClick={() => {
              const templateId = String(row?._id || row?.id || "").trim();
              if (!templateId) {
                return;
              }
              dispatch(
                updateTemplateStatusRequest({
                  id: templateId,
                  status: nextStatus,
                  listQuery,
                }),
              );
            }}
          >
            {nextStatus === "active" ? "Activate" : "Deactivate"}
          </Button>
        );
      },
    },
  ];

  return (
    <Box>
      {canCreate && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            overflow: "hidden",
            borderRadius: { xs: "14px", md: "18px" },
            border: "1px solid #eee5ff",
            background:
              "linear-gradient(180deg, #faf7ff 0%, #ffffff 42%, #ffffff 100%)",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 100%)",
              color: "#fff",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              Create reward template
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                opacity: 0.9,
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
              }}
            >
              This is what the customer sees on their lucky draw coupon.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2.5, md: 3 }}>
              <Grid item xs={12} lg={7}>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      sx={{ mb: 1, fontWeight: 600, color: "#4a4a4a", fontSize: 14 }}
                    >
                      Jeweller selection
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      value={form.jewellerScope}
                      onChange={(event, value) => {
                        if (!value) return;
                        setForm((prev) => ({
                          ...prev,
                          jewellerScope: value,
                          jewellers: value === "all" ? [] : prev.jewellers,
                        }));
                      }}
                      sx={{
                        gap: 1,
                        mb: form.jewellerScope === "custom" ? 1.5 : 0,
                        "& .MuiToggleButtonGroup-grouped": {
                          border: "1px solid #e0e0e0 !important",
                          borderRadius: "20px !important",
                          margin: 0,
                          px: 2.5,
                          textTransform: "none",
                          fontWeight: 600,
                        },
                      }}
                    >
                      <ToggleButton
                        value="all"
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#f3eaff !important",
                            color: "#7028DA !important",
                          },
                        }}
                      >
                        All
                      </ToggleButton>
                      <ToggleButton
                        value="custom"
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#f3eaff !important",
                            color: "#7028DA !important",
                          },
                        }}
                      >
                        Custom
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {form.jewellerScope === "custom" && (
                      <FormControl fullWidth sx={fieldSx}>
                        <InputLabel>Select jewellers</InputLabel>
                        <Select
                          multiple
                          value={form.jewellers}
                          onChange={(event) =>
                            onChange("jewellers", event.target.value)
                          }
                          input={<OutlinedInput label="Select jewellers" />}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => {
                                const jeweller = jewellerOptions.find(
                                  (item) => item.id === value || item._id === value,
                                );
                                return (
                                  <Chip
                                    key={value}
                                    size="small"
                                    label={jeweller?.jewellerName || value}
                                  />
                                );
                              })}
                            </Box>
                          )}
                          MenuProps={jewellerMenuProps}
                        >
                          {jewellerOptions.map((item) => (
                            <MenuItem
                              key={item.id || item._id}
                              value={item.id || item._id}
                            >
                              {item.jewellerName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    required
                    label="Title"
                    placeholder="e.g. Free silver coin"
                    value={form.title}
                    onChange={(event) => onChange("title", event.target.value)}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    label="Subtitle"
                    placeholder="Short line under the title"
                    value={form.subtitle}
                    onChange={(event) => onChange("subtitle", event.target.value)}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Description"
                    placeholder="Explain the reward in a few lines"
                    value={form.description}
                    onChange={(event) =>
                      onChange("description", event.target.value)
                    }
                    sx={fieldSx}
                  />
                  <Box>
                    <Typography
                      sx={{ mb: 1, fontWeight: 600, color: "#4a4a4a", fontSize: 14 }}
                    >
                      Custom message
                    </Typography>
                    <Typography sx={{ mb: 1, fontSize: 12, color: "#888" }}>
                      Used in push notification and coupon popup. Click a variable
                      to insert it.
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      useFlexGap
                      spacing={1}
                      sx={{ mb: 1.25 }}
                    >
                      {MESSAGE_VARIABLES.map((item) => (
                        <Chip
                          key={item.key}
                          size="small"
                          clickable
                          label={`{{${item.key}}}`}
                          onClick={() => insertMessageVariable(item.key)}
                          title={item.label}
                          sx={{
                            backgroundColor: "#f3eaff",
                            color: "#7028DA",
                            fontWeight: 600,
                            borderRadius: "8px",
                          }}
                        />
                      ))}
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Message"
                      placeholder="Hi {{customerName}}, you won {{rewardTitle}}! Your code is {{couponCode}}."
                      value={form.customMessage}
                      onChange={(event) =>
                        onChange("customMessage", event.target.value)
                      }
                      sx={fieldSx}
                      helperText="Example: Hi {{customerName}}, congratulations! Coupon {{couponCode}} unlocked."
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{ mb: 1, fontWeight: 600, color: "#4a4a4a", fontSize: 14 }}
                    >
                      Reward image
                    </Typography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <input
                        id="reward-template-image"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        hidden
                        onChange={onPickImage}
                      />
                      <label htmlFor="reward-template-image">
                        <Button
                          component="span"
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: "#d7c4ff",
                            color: "#7028DA",
                            py: 1.1,
                            px: 2,
                            width: { xs: "100%", sm: "auto" },
                          }}
                        >
                          Upload image
                        </Button>
                      </label>
                      {imageFile && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            px: 1.5,
                            py: 1,
                            borderRadius: "12px",
                            backgroundColor: "#f7f1ff",
                          }}
                        >
                          <Typography
                            sx={{
                              flex: 1,
                              fontSize: 13,
                              color: "#5b5b5b",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {imageFile.name}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={onRemoveImage}
                            sx={{ textTransform: "none", flexShrink: 0 }}
                          >
                            Remove
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                    <Typography sx={{ mt: 0.8, fontSize: 12, color: "#888" }}>
                      JPG, PNG or WEBP up to 5 MB
                    </Typography>
                  </Box>

                  <TextField
                    select
                    fullWidth
                    label="Type"
                    value={form.type}
                    onChange={(event) => onChange("type", event.target.value)}
                    sx={fieldSx}
                  >
                    <MenuItem value="lucky_draw">Lucky Draw</MenuItem>
                  </TextField>

                  <Box>
                    <Typography
                      sx={{ mb: 1, fontWeight: 600, color: "#4a4a4a", fontSize: 14 }}
                    >
                      Status
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      value={form.status}
                      onChange={(event, value) =>
                        value && onChange("status", value)
                      }
                      sx={{
                        gap: 1,
                        "& .MuiToggleButtonGroup-grouped": {
                          border: "1px solid #e0e0e0 !important",
                          borderRadius: "20px !important",
                          margin: 0,
                          px: 2.5,
                          textTransform: "none",
                          "&.Mui-selected": {
                            fontWeight: 700,
                          },
                        },
                      }}
                    >
                      <ToggleButton
                        value="active"
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#e8f5e9",
                            color: "#2e7d32",
                            borderColor: "#2e7d32 !important",
                          },
                        }}
                      >
                        Active
                      </ToggleButton>
                      <ToggleButton
                        value="inactive"
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#f5f5f5",
                            color: "#616161",
                            borderColor: "#9e9e9e !important",
                          },
                        }}
                      >
                        Inactive
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ pt: 1 }}
                  >
                    <Button
                      variant="contained"
                      disabled={creating}
                      onClick={onSubmit}
                      sx={{
                        minWidth: { sm: 180 },
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        py: 1.2,
                        background:
                          "linear-gradient(90deg, #7028DA 0%, #9E3ED7 100%)",
                        boxShadow: "0 8px 18px rgba(112, 40, 218, 0.28)",
                      }}
                    >
                      {creating ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={18} color="inherit" />
                          <span>Saving...</span>
                        </Stack>
                      ) : (
                        "Save template"
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={creating}
                      onClick={onReset}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 600,
                        py: 1.2,
                        borderColor: "#d7c4ff",
                        color: "#7028DA",
                      }}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Box
                  sx={{
                    height: "100%",
                    minHeight: { xs: 260, md: 320 },
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: "16px",
                    border: "1px dashed #d7c4ff",
                    background:
                      "linear-gradient(160deg, #ffffff 0%, #f7f1ff 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      mb: 1.5,
                      fontWeight: 700,
                      color: "#7028DA",
                      fontSize: 14,
                      letterSpacing: 0.3,
                    }}
                  >
                    Live preview
                  </Typography>

                  <Box
                    sx={{
                      borderRadius: "14px",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      boxShadow: "0 10px 24px rgba(112, 40, 218, 0.12)",
                    }}
                  >
                    <Box
                      sx={{
                        height: { xs: 140, sm: 160 },
                        background:
                          "linear-gradient(135deg, #efe5ff 0%, #f8f4ff 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {previewUrl && !imageBroken ? (
                        <Box
                          component="img"
                          src={previewUrl}
                          alt="Reward preview"
                          onError={() => setImageBroken(true)}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Stack alignItems="center" spacing={0.5} sx={{ color: "#9a7ad4" }}>
                          <ImageOutlinedIcon sx={{ fontSize: 42 }} />
                          <Typography sx={{ fontSize: 13 }}>
                            {previewUrl ? "Image could not load" : "Upload an image"}
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Chip
                          size="small"
                          label={form.type}
                          sx={{
                            textTransform: "capitalize",
                            backgroundColor: "#f3eaff",
                            color: "#7028DA",
                            fontWeight: 700,
                          }}
                        />
                        <Chip
                          size="small"
                          label={form.status}
                          color={form.status === "active" ? "success" : "default"}
                          sx={{ textTransform: "capitalize", fontWeight: 600 }}
                        />
                      </Stack>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "1.05rem", sm: "1.15rem" },
                          color: "#2d2d2d",
                          wordBreak: "break-word",
                        }}
                      >
                        {form.title || "Reward title"}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.5,
                          color: "#6f6f6f",
                          fontSize: 14,
                          wordBreak: "break-word",
                        }}
                      >
                        {form.subtitle || "Subtitle will show here"}
                      </Typography>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography
                        sx={{
                          color: "#555",
                          fontSize: 13,
                          lineHeight: 1.5,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          minHeight: 40,
                        }}
                      >
                        {form.description ||
                          "Write a short description so customers understand the reward."}
                      </Typography>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#8a7aa8",
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          mb: 0.5,
                        }}
                      >
                        Message preview
                      </Typography>
                      <Typography
                        sx={{
                          color: "#7028DA",
                          fontSize: 13,
                          lineHeight: 1.5,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          fontWeight: 600,
                        }}
                      >
                        {previewResolvedMessage(form.customMessage) ||
                          "Add a custom message with variables like {{customerName}}."}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      <Box
        sx={{
          borderRadius: { xs: "14px", md: "18px" },
          border: "1px solid #f0f0f0",
          p: { xs: 1, sm: 2 },
          backgroundColor: "#fff",
        }}
      >
        <Typography
          sx={{
            mb: 1.5,
            px: { xs: 1, sm: 0 },
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.1rem" },
            color: "#333",
          }}
        >
          Saved templates
        </Typography>
        <TableCompo
          columns={columns}
          data={rows}
          loading={templatesLoading}
          page={pagination.page}
          limit={pagination.limit}
          totalCount={templateTotal}
          multiline
          estimatedRowHeight={88}
          onTableChange={(action, tableState) => {
            if (action === "changePage") {
              setPagination((prev) => ({ ...prev, page: tableState.page }));
            }
            if (action === "changeRowsPerPage") {
              setPagination({ page: 0, limit: tableState.rowsPerPage });
            }
            if (action === "search") {
              setSearch(tableState.searchText || "");
              setPagination((prev) => ({ ...prev, page: 0 }));
            }
          }}
        />
      </Box>

      <Dialog
        open={viewImage.open}
        onClose={() => setViewImage({ open: false, url: "", title: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700, pr: 2 }}>
            {viewImage.title || "Reward image"}
          </Typography>
          <IconButton
            onClick={() => setViewImage({ open: false, url: "", title: "" })}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewImage.url ? (
            <Box
              component="img"
              src={viewImage.url}
              alt={viewImage.title || "Reward"}
              sx={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "12px",
                backgroundColor: "#faf7ff",
              }}
            />
          ) : (
            <Typography>No image available</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => window.open(viewImage.url, "_blank")}
            disabled={!viewImage.url}
            sx={{ textTransform: "none", color: "#7028DA", fontWeight: 600 }}
          >
            Open in new tab
          </Button>
          <Button
            variant="contained"
            onClick={() => setViewImage({ open: false, url: "", title: "" })}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 100%)",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
